import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { getUserRoles } from '../../utils/roleHelpers';
import { getDealById, getCurrentPIFSubmission } from '../../services/dealService';
import { getFundById } from '../../services/firestoreService';
import PortalLayout from '../../components/shared/Layout/PortalLayout';
import AnalystReviewForm from '../../components/forms/AnalystReviewForm';
import PIFScoreCard from '../../components/pipeline/PIFScoreCard';
import AISummaryPanel from '../../components/pipeline/AISummaryPanel';
import Button from '../../components/shared/UI/Button';
import LoadingSpinner from '../../components/shared/UI/LoadingSpinner';

export default function AnalystReviewPage() {
  const { dealId } = useParams();
  const navigate = useNavigate();
  const { user, userProfile } = useAuth();
  const [deal, setDeal] = useState(null);
  const [fund, setFund] = useState(null);
  const [submission, setSubmission] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  useEffect(() => {
    async function load() {
      try {
        const d = await getDealById(dealId);
        if (!d) { setLoading(false); return; }
        setDeal(d);
        const [f, s] = await Promise.all([
          getFundById(d.fund_id),
          getCurrentPIFSubmission(dealId),
        ]);
        setFund(f);
        setSubmission(s);
      } catch (err) {
        console.error('AnalystReviewPage load error:', err);
        setLoadError(err.message || 'Failed to load deal data');
      } finally {
        setLoading(false);
      }
    }
    if (dealId) load();
  }, [dealId]);

  if (loading) return <PortalLayout><LoadingSpinner /></PortalLayout>;

  if (loadError) {
    return (
      <PortalLayout>
        <div className="alert alert-error">
          Unable to load review form. Please try refreshing the page. If the
          problem persists, contact your administrator.
          <p style={{ fontSize: 12, marginTop: 6, opacity: 0.7 }}>{loadError}</p>
        </div>
        <Button variant="secondary" onClick={() => navigate('/pipeline')} style={{ marginTop: 12 }}>
          ← Back to Pipeline
        </Button>
      </PortalLayout>
    );
  }

  if (!deal) {
    return (
      <PortalLayout>
        <div className="alert alert-error">Deal not found.</div>
        <Button variant="secondary" onClick={() => navigate('/pipeline')} style={{ marginTop: 12 }}>
          ← Back to Pipeline
        </Button>
      </PortalLayout>
    );
  }

  // Allow assigned analyst OR any principal to access the review page
  const userRoles = getUserRoles(userProfile);
  const isPrincipal = ['head_of_pipeline', 'cio', 'md'].some(r => userRoles.includes(r));
  const isAssignedAnalyst = deal.assigned_analyst_id === user?.uid;

  if (!isAssignedAnalyst && !isPrincipal) {
    return (
      <PortalLayout>
        <div className="alert alert-error">
          You are not assigned as the analyst for this deal.
        </div>
        <Button variant="secondary" onClick={() => navigate(`/pipeline/${dealId}`)} style={{ marginTop: 12 }}>
          ← Back to Deal
        </Button>
      </PortalLayout>
    );
  }

  return (
    <PortalLayout>
      <div style={{ marginBottom: 16 }}>
        <Button variant="ghost" size="sm" onClick={() => navigate(`/pipeline/${dealId}`)}>
          ← {deal.company_name}
        </Button>
      </div>
      <h1 style={{ fontSize: 20, fontWeight: 700, marginBottom: 6 }}>Analyst Review</h1>
      <p style={{ fontSize: 14, color: 'var(--color-text-secondary)', marginBottom: 24 }}>
        {deal.company_name}
      </p>

      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 24 }}>
        <div>
          <PIFScoreCard submission={submission} rubric={null} />
          {submission && (
            <div style={{ marginTop: 16 }}>
              <AISummaryPanel submission={submission} dealId={dealId} />
            </div>
          )}
        </div>
        <AnalystReviewForm
          deal={deal}
          fund={fund}
          reviewType="primary_analyst"
          onSaved={() => navigate(`/pipeline/${dealId}`)}
        />
      </div>
    </PortalLayout>
  );
}
